/** EventConnection: start(onEvent), stop(). Owns one authenticated wire lifecycle.
 * Dependencies: socketFactory, scheduler(set/clear), lifecycle(state/subscribe),
 * protocol(authenticate/decode/delivers) and retryDelay(attempt). No React/DOM/HTTP.
 */
export default class SocketConnection {
  #socketFactory;
  #scheduler;
  #lifecycle;
  #protocol;
  #retryDelay;
  #token;
  #socket;
  #retry;
  #watchdog;
  #attempt;
  #stopped;
  #denied;
  #emit;
  #unsubscribe;
  constructor({ socketFactory, scheduler, lifecycle, protocol, retryDelay, token }) {
    this.#socketFactory = socketFactory;
    this.#scheduler = scheduler;
    this.#lifecycle = lifecycle;
    this.#protocol = protocol;
    this.#retryDelay = retryDelay;
    this.#token = token;
    this.#socket = null;
    this.#retry = null;
    this.#watchdog = null;
    this.#attempt = 0;
    this.#stopped = true;
    this.#denied = false;
    this.#emit = () => {};
  }

  start(onEvent) {
    if (!this.#stopped) return;
    this.#stopped = false;
    this.#emit = onEvent;
    this.#unsubscribe = this.#lifecycle.subscribe(state => this.#environmentChanged(state));
    this.#environmentChanged(this.#lifecycle.state());
  }

  stop() {
    if (this.#stopped) return;
    this.#stopped = true;
    this.#disconnect();
    this.#unsubscribe?.();
    this.#emit = () => {};
  }

  #status(status) { this.#emit({ type: 'connection', status }); }

  #disconnect() {
    this.#scheduler.clear(this.#retry); this.#scheduler.clear(this.#watchdog);
    const previous = this.#socket; this.#socket = null;
    if (previous) {
      previous.onopen = previous.onmessage = previous.onclose = previous.onerror = null;
      previous.close();
    }
  }

  #environmentChanged(state) {
    if (this.#stopped) return;
    if (state !== 'available') {
      this.#disconnect(); this.#status(state === 'paused' ? 'paused' : 'reconnecting');
    } else if (!this.#socket && !this.#denied) this.#connect();
  }

  #schedule() {
    if (this.#stopped || this.#denied || this.#lifecycle.state() !== 'available') return;
    this.#status('reconnecting');
    this.#scheduler.clear(this.#retry);
    this.#retry = this.#scheduler.set(() => this.#connect(), this.#retryDelay(this.#attempt++));
  }

  #armWatchdog(delay) {
    this.#scheduler.clear(this.#watchdog);
    this.#watchdog = this.#scheduler.set(() => { this.#disconnect(); this.#schedule(); }, delay);
  }

  #connect() {
    if (this.#stopped || this.#denied || this.#lifecycle.state() !== 'available') return;
    this.#disconnect(); this.#status('connecting');
    try {
      const current = this.#socketFactory(); this.#socket = current;
      this.#armWatchdog(10000);
      current.onopen = () => {
        if (this.#socket !== current) return;
        try { current.send(this.#protocol.authenticate(this.#token)); }
        catch { this.#disconnect(); this.#schedule(); }
      };
      current.onmessage = message => {
        if (this.#socket !== current) return;
        const event = this.#protocol.decode(message.data);
        if (!event) return;
        this.#armWatchdog(60000);
        if (event.type === 'ready') { this.#attempt = 0; this.#status('connected'); }
        if (this.#protocol.delivers(event)) this.#emit(event);
      };
      current.onclose = event => {
        if (this.#socket !== current) return;
        this.#socket = null; this.#scheduler.clear(this.#watchdog);
        if ([4401, 1008].includes(event.code)) { this.#denied = true; this.#status('unauthorized'); }
        else this.#schedule();
      };
      current.onerror = () => {
        if (this.#socket === current) { this.#disconnect(); this.#schedule(); }
      };
    } catch { this.#schedule(); }
  }
}
